import { useEffect, useState, Suspense, lazy } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";

import { getHomeSeo } from "../config/seo";
import { Loading } from "../components/Loading";
const Hero = lazy(() => import("../components/Hero"));
const Services = lazy(() => import("../components/Services"));
const MaskClip = lazy(() => import("../components/MaskClip"));
const Carousel = lazy(() => import("../components/Carousel"));
import { Footer } from "../components/Footer";
import { Clients } from "../components/Clients";

export const HomePage = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [footerVisible, setFooterVisible] = useState(false);
  const { i18n } = useTranslation();
  const { title, description, keywords, canonical } = getHomeSeo(i18n.language);

  useEffect(() => {
    // Observer para saber si el footer está en pantalla
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setFooterVisible(entry.isIntersecting);
      });
    });

    const footerElement = document.querySelector("#idFooter");
    if (footerElement) {
      observer.observe(footerElement);
    }

    // Listener de scroll para controlar el botón "scroll to top"
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);

    // Cleanup: desconectar observer y listener
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const ScrollToTopButton = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const showScrollButton = scrollPosition >= 100 && !footerVisible;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        {keywords && <meta name="keywords" content={keywords} />}
        <link rel="canonical" href={canonical} />

        {/* Opcional: Open Graph básico */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
      </Helmet>
      <div id="idHome" className="overflow-hidden">
        <div className="pt-16"></div>

        <Suspense fallback={<Loading />}>
          <Hero />
        </Suspense>

        <Suspense fallback={<Loading />}>
          <Services />
        </Suspense>

        <Suspense fallback={<Loading />}>
          <Carousel />
        </Suspense>

        <Suspense fallback={<Loading />}>
          <MaskClip />
        </Suspense>

        <Clients />
        <Footer />

        <button
          tabIndex={0}
          aria-label="Scroll to top"
          onClick={ScrollToTopButton}
          className={`z-50 scroll-to-top fixed bottom-4 right-2 md:right-4 rounded-full
            transition duration-300 hover:bg-gray-700 hover:text-gray-200
            ${showScrollButton ? "visible" : "invisible"}`}
        >
          <img
            loading="lazy"
            width={12}
            height={12}
            src="/assets/homepage/toparrow.svg"
            className="w-10 md:w-12 rounded-full bg-gray-200"
            alt="Top Arrow"
          />
        </button>
      </div>
    </>
  );
};